import React, { useState, useEffect } from 'react';
import { View, Text, useWindowDimensions, Platform } from 'react-native';
import Head from 'expo-router/head';
import AnimatedPressable from '@/components/AnimatedPressable';
import { Redirect, useRouter, type Href } from 'expo-router';
import { Check, X, Crown } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useTrackScreen } from '@/lib/analytics';
import { PublicLayout } from '@/components/PublicLayout';
import { modeFeatures } from '@/lib/config/app-mode';
import {
  startupPricing,
  investorPricing,
  CURRENCY,
  getYearlySavingsPercent,
  type PricingTier,
} from '@/lib/pricing';

type Tab = 'startups' | 'investors';
type BillingCycle = 'monthly' | 'yearly';

/** The public site publishes no prices during the beta — see modeFeatures.showPublicPricing. */
export default function PricingRoute() {
  if (!modeFeatures.showPublicPricing) return <Redirect href="/" />;
  return <PricingScreen />;
}

function PricingScreen() {
  useTrackScreen('Pricing');
  const router = useRouter();

  // FAQPage JSON-LD structured data for pricing FAQs
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'pricing-faq-jsonld';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Can I switch plans?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, you can upgrade or downgrade at any time. The change takes effect immediately, and the price difference for the rest of your current billing period is prorated on your next invoice.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is there a free trial?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'There is no free trial of paid plans, but startups can use the Free plan indefinitely. You can cancel a paid plan at any time and keep it until the end of the period you have paid for. Fees for a billing period that has started are not refunded, except where the law requires it. See our Refund Policy at https://inverio.ai/refunds.',
          },
        },
        {
          '@type': 'Question',
          name: 'What payment methods do you accept?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'We accept all major credit and debit cards through Stripe. All prices are in Swiss Francs (CHF).',
          },
        },
      ],
    });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('pricing-faq-jsonld');
      if (existing) existing.remove();
    };
  }, []);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const [activeTab, setActiveTab] = useState<Tab>('startups');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  const tiers = activeTab === 'startups' ? startupPricing : investorPricing;

  return (
    <PublicLayout title="Pricing" maxWidth={1000}>
      {Platform.OS === 'web' && (
        <Head>
          <title>Pricing — Inverio</title>
          <meta name="description" content="Simple, transparent pricing for startups and investors. Start free, upgrade as you grow. All prices in CHF." />
          <link rel="canonical" href="https://inverio.ai/pricing" />
          <meta property="og:title" content="Pricing — Inverio" />
          <meta property="og:description" content="Simple, transparent pricing for startups and investors. Start free, upgrade as you grow. All prices in CHF." />
          <meta property="og:url" content="https://inverio.ai/pricing" />
        </Head>
      )}
      {/* Subtitle */}
      <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-6">
        <Text style={{ color: 'rgba(255,255,255,0.6)' }} className="text-sm leading-relaxed">
          Simple, transparent pricing. Start free and upgrade as you grow.
        </Text>
      </Animated.View>

      {/* Tab Toggle */}
      <Animated.View entering={FadeInDown.delay(150).duration(500)} className="mb-5">
        <View
          className="flex-row rounded-xl p-1"
          style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)' }}
        >
          <AnimatedPressable
            onPress={() => setActiveTab('startups')}
            className="flex-1 py-2.5 rounded-lg items-center"
            style={activeTab === 'startups' ? { backgroundColor: '#4fd1c5' } : undefined}
          >
            <Text
              style={{ color: activeTab === 'startups' ? '#060e1a' : 'rgba(255,255,255,0.6)' }}
              className="font-semibold text-sm"
            >
              For Startups
            </Text>
          </AnimatedPressable>
          <AnimatedPressable
            onPress={() => setActiveTab('investors')}
            className="flex-1 py-2.5 rounded-lg items-center"
            style={activeTab === 'investors' ? { backgroundColor: '#818cf8' } : undefined}
          >
            <Text
              style={{ color: activeTab === 'investors' ? '#060e1a' : 'rgba(255,255,255,0.6)' }}
              className="font-semibold text-sm"
            >
              For Investors
            </Text>
          </AnimatedPressable>
        </View>
      </Animated.View>

      {/* Billing Toggle */}
      <Animated.View entering={FadeInDown.delay(200).duration(500)} className="flex-row items-center justify-center gap-3 mb-6">
        <AnimatedPressable onPress={() => setBillingCycle('monthly')}>
          <Text
            style={{ color: billingCycle === 'monthly' ? '#4fd1c5' : 'rgba(255,255,255,0.38)' }}
            className="text-sm font-semibold"
          >
            Monthly
          </Text>
        </AnimatedPressable>
        <View className="w-px h-4" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }} />
        <AnimatedPressable onPress={() => setBillingCycle('yearly')}>
          <Text
            style={{ color: billingCycle === 'yearly' ? '#4fd1c5' : 'rgba(255,255,255,0.38)' }}
            className="text-sm font-semibold"
          >
            Yearly (save up to 20%)
          </Text>
        </AnimatedPressable>
      </Animated.View>

      {/* Pricing Cards */}
      <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 16, flexWrap: 'wrap' }}>
        {tiers.map((tier, index) => (
          <Animated.View
            key={tier.id}
            entering={FadeInDown.delay(250 + index * 80).duration(500)}
            style={{ flex: isMobile ? undefined : 1, minWidth: isMobile ? undefined : 200 }}
          >
            <PricingCard
              tier={tier}
              billingCycle={billingCycle}
              onSelect={() => router.push('/login')}
            />
          </Animated.View>
        ))}
      </View>

      {/* FAQ */}
      <Animated.View entering={FadeInDown.delay(500).duration(500)} className="mt-8">
        <Text style={{ color: '#FFFFFF' }} className="text-lg font-bold mb-4">
          Frequently Asked Questions
        </Text>
        <View className="gap-3">
          <FAQItem
            q="Can I switch plans?"
            a="Yes, you can upgrade or downgrade at any time. The change takes effect immediately, and the price difference for the rest of your current billing period is prorated on your next invoice."
          />
          <FAQItem
            q="Is there a free trial?"
            a="There is no free trial of paid plans, but startups can use the Free plan indefinitely. You can cancel a paid plan at any time and keep it until the end of the period you have paid for. Fees for a billing period that has started are not refunded, except where the law requires it."
            link={{ label: 'Read our Refund Policy', href: '/refunds' }}
          />
          <FAQItem
            q="What payment methods do you accept?"
            a="We accept all major credit and debit cards through Stripe. All prices are in Swiss Francs (CHF)."
          />
        </View>
      </Animated.View>
    </PublicLayout>
  );
}

function PricingCard({
  tier,
  billingCycle,
  onSelect,
}: {
  tier: PricingTier;
  billingCycle: BillingCycle;
  onSelect: () => void;
}) {
  const displayPrice = billingCycle === 'yearly' && tier.yearlyPrice ? tier.yearlyPrice : tier.price;
  const savingsPercent = tier.yearlyPrice ? getYearlySavingsPercent(tier.price, tier.yearlyPrice) : 0;

  return (
    <View
      className="p-5 rounded-2xl"
      style={{
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderWidth: tier.highlighted ? 2 : 1,
        borderColor: tier.highlighted ? '#4fd1c5' : 'rgba(255,255,255,0.08)',
      }}
    >
      {tier.popular && (
        <View className="flex-row items-center gap-1 mb-3">
          <Crown size={14} color={'#4fd1c5'} />
          <Text style={{ color: '#4fd1c5' }} className="text-xs font-bold uppercase tracking-wide">
            Most Popular
          </Text>
        </View>
      )}

      <Text style={{ color: '#FFFFFF' }} className="text-lg font-bold">
        {tier.name}
      </Text>
      <Text style={{ color: 'rgba(255,255,255,0.38)' }} className="text-xs mb-3">
        {tier.tagline}
      </Text>

      <View className="flex-row items-baseline gap-1 mb-4">
        <Text style={{ color: '#FFFFFF', fontSize: 28 }} className="font-bold">
          {displayPrice === 0 ? 'Free' : `${CURRENCY} ${displayPrice}`}
        </Text>
        {displayPrice > 0 && (
          <Text style={{ color: 'rgba(255,255,255,0.38)' }} className="text-sm">/mo</Text>
        )}
        {billingCycle === 'yearly' && savingsPercent > 0 && (
          <View className="ml-2 px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(79,209,197,0.15)' }}>
            <Text style={{ color: '#4fd1c5' }} className="text-xs font-semibold">-{savingsPercent}%</Text>
          </View>
        )}
      </View>

      <AnimatedPressable
        onPress={onSelect}
        className="py-3 rounded-xl items-center mb-4"
        style={{
          backgroundColor: tier.highlighted ? '#4fd1c5' : '#0f1f38',
          borderWidth: tier.highlighted ? 0 : 1,
          borderColor: 'rgba(255,255,255,0.08)',
        }}
      >
        <Text
          style={{ color: tier.highlighted ? '#060e1a' : '#FFFFFF' }}
          className="font-semibold text-sm"
        >
          {tier.ctaText}
        </Text>
      </AnimatedPressable>

      <View className="gap-2">
        {tier.features.map((feature, i) => (
          <View key={i} className="flex-row items-start gap-2">
            <Check size={14} color={'#4fd1c5'} style={{ marginTop: 2 }} />
            <Text style={{ color: 'rgba(255,255,255,0.6)' }} className="text-xs flex-1 leading-relaxed">
              {feature}
            </Text>
          </View>
        ))}
        {tier.limitations?.map((limit, i) => (
          <View key={`l-${i}`} className="flex-row items-start gap-2">
            <X size={14} color={'rgba(255,255,255,0.38)'} style={{ marginTop: 2 }} />
            <Text style={{ color: 'rgba(255,255,255,0.38)' }} className="text-xs flex-1 leading-relaxed">
              {limit}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function FAQItem({
  q,
  a,
  link,
}: {
  q: string;
  a: string;
  /** Optional in-app link rendered after the answer. */
  link?: { label: string; href: Href };
}) {
  const router = useRouter();
  return (
    <View
      className="p-4 rounded-xl"
      style={{
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
      }}
    >
      <Text style={{ color: '#FFFFFF' }} className="font-semibold text-sm mb-1">{q}</Text>
      <Text style={{ color: 'rgba(255,255,255,0.6)' }} className="text-xs leading-relaxed">
        {a}
        {link ? (
          <>
            {' '}
            <Text
              accessibilityRole="link"
              onPress={() => router.push(link.href)}
              style={{ color: '#4fd1c5', textDecorationLine: 'underline' }}
              className="font-semibold"
            >
              {link.label}
            </Text>
          </>
        ) : null}
      </Text>
    </View>
  );
}
